import React from "react";
import { Box, Typography, useTheme, Tooltip, Avatar } from "@mui/material";
import CallReceivedIcon from "@mui/icons-material/CallReceived";
import CallMadeIcon from "@mui/icons-material/CallMade";
import AccountTreeOutlinedIcon from "@mui/icons-material/AccountTreeOutlined";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import { nameToContactAvatarImage, NON_DRAGGABLE_AVATAR_IMG_PROPS } from "../../../../config/AvatarColor";
import { formatMessageTime } from "../../../../utils/timestampUtils";
import { formatLastMessagePreview } from "../../../../utils/formatLastMessagePreview";

const MESSAGE_PREVIEW_MAX_CHARS = 32;
const NAME_MAX_CHARS = 15;

// Shared char-count truncator (same "…" pattern as truncateMessage below) — used for
// contact/assignee/closed-by names so a very long one can never stretch the row wide
// enough to spill into whatever card sits beside this panel. Applied to BOTH the inline
// text and the hover-tooltip text, so the tooltip itself never grows past this either.
const truncateName = (text) => (text.length > NAME_MAX_CHARS ? `${text.slice(0, NAME_MAX_CHARS)}…` : text);

// Smaller sibling of ContactsPanel.jsx's TAB_TOOLTIP_SX (same dark-bubble convention,
// tighter maxWidth/padding/font since this one hangs off a single-line row label, not a
// section tab).
const ASSIGNEE_TOOLTIP_SX = {
  tooltip: {
    sx: {
      backgroundColor: (theme) => (theme.palette.mode === "dark" ? "#E2E2E4" : "#1A1A1A"),
      color: (theme) => (theme.palette.mode === "dark" ? "#1A1A1A" : "#F5F5F5"),
      fontSize: "0.65rem",
      fontWeight: 500,
      borderRadius: "6px",
      maxWidth: 130,
      textAlign: "center",
      px: 0.75,
      py: 0.4,
      boxShadow: (theme) =>
        theme.palette.mode === "dark" ? "0 6px 16px rgba(0,0,0,0.35)" : "0 4px 12px rgba(0,0,0,0.15)",
    },
  },
  arrow: {
    sx: {
      color: (theme) => (theme.palette.mode === "dark" ? "#E2E2E4" : "#1A1A1A"),
    },
  },
  // Pulls the tooltip a little closer to the row than MUI's default gap.
  popper: {
    // Negative distance — the anchor (the small avatar+name Box) sits well above the row's
    // own bottom edge (row has generous py), so a small positive offset still left a big
    // visual gap. Pulling it up into that empty padding space instead.
    modifiers: [{ name: "offset", options: { offset: [0, -10] } }],
  },
};

// Hard-caps the last-message preview at 32 chars (design spec) — the row's own CSS
// ellipsis (whiteSpace/overflow/textOverflow below) is a width-based fallback, kept as a
// safety net, but shouldn't be relied on alone since row width can vary.
const truncateMessage = (text) =>
  text.length > MESSAGE_PREVIEW_MAX_CHARS ? `${text.slice(0, MESSAGE_PREVIEW_MAX_CHARS)}…` : text;

// Button/list messages store their raw WhatsApp interactive payload as JSON in `body`
// (see sendInteractiveButtons.service.js / sendListMessage.service.js) — `content` from
// getinbox is just the placeholder label ("LIST") or, for button, the same raw JSON. Pull
// the human-readable text (the interactive payload's own `body` field) out of that JSON
// instead of previewing the label or the JSON blob.
//
// Everything else falls through to the SAME shared formatter MessageCard.jsx's Inbox
// chat list uses (formatLastMessagePreview.js) — this used to just return
// `lastMessage.content || lastMessage.body || ""` unconditionally for every other type,
// which meant a location message previewed its raw `{"latitude":...,"longitude":...}`
// JSON straight into the row (Rahim, 2026-09-10: "it shows weird latitude code"), and
// the same was true for images/video/audio/documents (a raw media URL), shared contacts,
// product-catalog orders, and WhatsApp-Ad referrals — none of those had a readable label
// here at all. See that util's own comment for the full history.
const getLastMessageText = (lastMessage) => {
  if (!lastMessage) return "";
  const type = lastMessage.type || lastMessage.messageType;
  if (type === "list" || type === "button" || type === "interactive") {
    const raw = lastMessage.body || lastMessage.message;
    if (typeof raw === "string" && raw.trim().startsWith("{")) {
      try {
        const parsed = JSON.parse(raw);
        const text = typeof parsed.body === "string" ? parsed.body : parsed.body?.text;
        if (text) return text;
      } catch {
        // Not JSON — fall through to the shared formatter below.
      }
    }
  }
  return formatLastMessagePreview(lastMessage);
};

// Renders one contact row for the Dashboard's ContactsPanel. `contact` is the same
// hydrated record GET /api/inbox/getinbox returns per contact (see inboxController.js) —
// no extra shape translation happens between the API and this row.
function ContactRow({ contact, channelIcons, statusColor, isLast, onClick }) {
  const theme = useTheme();
  const channel = channelIcons[contact.platform] || channelIcons.whatsapp;
  const ChannelIcon = channel.Icon;
  const messagePreview = truncateMessage(getLastMessageText(contact.lastMessage));
  const timeLabel = contact.lastMessage?.timestamp ? formatMessageTime(contact.lastMessage.timestamp) : "";
  const avatarImage = nameToContactAvatarImage(contact.name);

  return (
    <Box
      onClick={onClick}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.6,
        py: 1.7,
        px: 1.25,
        mx: -1.25,
        cursor: "pointer",
        transition: "background-color 0.15s ease",
        borderBottom: !isLast ? `1px solid ${theme.palette.divider}` : "none",
        "&:hover": { backgroundColor: theme.palette.action.hover },
      }}
    >
      <Avatar
        src={avatarImage}
        imgProps={NON_DRAGGABLE_AVATAR_IMG_PROPS}
        sx={{ width: 43, height: 43, flexShrink: 0, border: (t) => `1px solid ${t.palette.divider}` }}
      />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{ fontSize: "0.9375rem", fontWeight: 500, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{truncateName(contact.name)}</Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 0.25 }}>
          <Box sx={{ width: 21, height: 21, borderRadius: "50%", background: channel.bg, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            <ChannelIcon size={12} color="#fff" />
          </Box>
          <Typography sx={{ fontSize: "0.9375rem", color: "text.secondary", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
            {messagePreview}
          </Typography>
          {contact.lastMessage && (
            <Tooltip title={contact.lastMessageFromContact ? "Received" : "Sent"}>
              {contact.lastMessageFromContact ? (
                <CallReceivedIcon sx={{ fontSize: 13, color: "#22c55e", flexShrink: 0 }} />
              ) : (
                <CallMadeIcon sx={{ fontSize: 13, color: "#f87171", flexShrink: 0 }} />
              )}
            </Tooltip>
          )}
        </Box>
      </Box>
      <Box sx={{ textAlign: "right", flexShrink: 0 }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 0.4 }}>
          <Typography sx={{ fontSize: "0.8125rem", lineHeight: 1, color: "text.secondary" }}>{timeLabel}</Typography>
          <AccessTimeIcon sx={{ fontSize: 13, color: "text.secondary" }} />
        </Box>
        {contact.status === "Assigned" && contact.assignedToName ? (
          <Tooltip title={`Assigned to ${truncateName(contact.assignedToName)}`} placement="bottom" arrow componentsProps={ASSIGNEE_TOOLTIP_SX}>
            <Box sx={{ mt: 0.375, display: "inline-flex", alignItems: "center", justifyContent: "flex-end", gap: 0.6 }}>
              <Box sx={{ width: 28, height: 28, borderRadius: "50%", backgroundColor: "#f97316", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "0.75rem", fontWeight: 700, flexShrink: 0 }}>
                {contact.assignedToName.trim()[0]?.toUpperCase() || "?"}
              </Box>
              <Typography sx={{ fontSize: "0.8125rem", fontWeight: 600, color: "text.primary", maxWidth: 120, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                {truncateName(contact.assignedToName)}
              </Typography>
            </Box>
          </Tooltip>
        ) : contact.status === "Closed" ? (
          // closedByName is resolved server-side from sessionHistory.completedBy (falling
          // back to the closechat log for older contacts) — see inboxController.js. No name
          // means no team member is attributable to the close, i.e. a workflow closed it.
          <Tooltip
            title={contact.closedByName ? `Closed by ${truncateName(contact.closedByName)}` : "Closed by workflow"}
            placement="bottom"
            arrow
            componentsProps={ASSIGNEE_TOOLTIP_SX}
          >
            <Box sx={{ mt: 0.375, display: "inline-flex", alignItems: "center", justifyContent: "flex-end", gap: 0.6 }}>
              <Box sx={{ width: 28, height: 28, borderRadius: "50%", backgroundColor: statusColor.Closed, color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "0.75rem", fontWeight: 700, flexShrink: 0 }}>
                {contact.closedByName
                  ? contact.closedByName.trim()[0]?.toUpperCase() || "?"
                  : <AccountTreeOutlinedIcon sx={{ fontSize: 15 }} />}
              </Box>
              <Typography sx={{ fontSize: "0.8125rem", fontWeight: 600, color: "text.primary", maxWidth: 120, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                {truncateName(contact.closedByName || "Workflow")}
              </Typography>
            </Box>
          </Tooltip>
        ) : null}
      </Box>
    </Box>
  );
}

export default ContactRow;
